import {
  ControlledFormSegmentedControl,
  type SegmentedControlOption,
} from '@components/formInputs/ControlledFormSegmentedControl';
import cx from 'classnames';
import { useFormContext, useWatch } from 'react-hook-form';
import typography from '~styles/typography.scss';
import { SettingsOutletHeader } from '../../shared/SettingsOutletHeader';
import { type ProcessingFeePayer } from '../../team/apiHelpers';
import { type GeneralSettingsForm } from '../utils/generalSettingsFormTypes';
import layoutStyles from './ProcessingFeeSection.scss';

const PROCESSING_FEE_BODY =
  'The processing fee is charged on ticketed event and table purchases. Choose whether guests pay it on top of the price, or your restaurant pays it.';

const PROCESSING_FEE_PAYER_OPTIONS: SegmentedControlOption<ProcessingFeePayer>[] =
  [
    { label: 'Guest', value: 'guest' },
    { label: 'Restaurant', value: 'restaurant' },
  ];

const PROCESSING_FEE_PAYER_EXPLANATION: Record<ProcessingFeePayer, string> = {
  guest: 'Guests pay the price plus the processing fee.',
  restaurant:
    'Guests pay only the price, with no processing fee. The fee is deducted from the amount your restaurant receives for each purchase.',
};

const HELPER_ID = 'processing-fee-payer-helper';

export interface ProcessingFeeSectionProps {
  sectionTitleId: string;
}

export const ProcessingFeeSection = ({
  sectionTitleId,
}: ProcessingFeeSectionProps) => {
  const { control } = useFormContext<GeneralSettingsForm>();
  const processingFeePayer = useWatch({ control, name: 'processingFeePayer' });

  return (
    <section className={layoutStyles.section} aria-labelledby={sectionTitleId}>
      <SettingsOutletHeader
        variant="section"
        titleId={sectionTitleId}
        title="Processing fee"
        subtitle={PROCESSING_FEE_BODY}
      />
      <div className={layoutStyles.formFields}>
        <ControlledFormSegmentedControl<GeneralSettingsForm, ProcessingFeePayer>
          control={control}
          name="processingFeePayer"
          label="Who pays the processing fee"
          options={PROCESSING_FEE_PAYER_OPTIONS}
          describedById={HELPER_ID}
        />
        <p
          aria-live="polite"
          className={cx(typography.c3, layoutStyles.helperText)}
          id={HELPER_ID}
        >
          {PROCESSING_FEE_PAYER_EXPLANATION[processingFeePayer]}
        </p>
      </div>
    </section>
  );
};
